/*
  Toothpaste page, variant A (templates/product.zp-a.json, sections/zpa-*.liquid).

  1. The waitlist form (sections/zpa-hero.liquid). Same endpoint and payload
     as the coffee page's waitlist (assets/coffee-hero-product.js): the worker
     validates the e-mail, requires consent, rate-limits, keeps the address on
     the product's list and tags the Shopify customer. Answer { ok: true }.
  2. The gallery: thumbnails scroll the panel, a swipe marks the thumbnail.
  3. Wide screens: where the buy column pins (under the header, or by its
     bottom when taller than the screen; it stops above the coffees), as the
     coffee page does it. The details section's two columns ride along too.
  4. Below 1100 px: the bar at the bottom while the form is out of view.
  5. Links to an anchor on the page scroll there; a question opens.

  A module: it runs once however many times its tag appears.
*/
const ENDPOINT = 'https://evelina.kavaevelina.workers.dev/api/waitlist/subscribe';
const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const CHECK =
  '<svg class="zpa-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12.5l4.4 4.3L19 7.2"/></svg>';

/* ── 1. Waitlist ── */
/** @param {HTMLFormElement} form */
function done(form) {
  const box = document.createElement('div');
  box.className = 'zpa-wl__done';
  box.setAttribute('role', 'status');
  box.innerHTML = '<span class="zpa-wl__done-mark">' + CHECK + '</span><span></span>';
  /** @type {HTMLElement} */ (box.lastElementChild).textContent = form.dataset.success || '';
  form.replaceChildren(box);
  form.dataset.sent = '1';
  document.dispatchEvent(new CustomEvent('zpa:sent'));
}

/** @param {HTMLFormElement} form @param {string} text */
function say(form, text) {
  const el = /** @type {HTMLElement | null} */ (form.querySelector('[data-zpa-wl-msg]'));
  if (!el) return;
  el.textContent = text;
  el.hidden = !text;
}

/** @param {HTMLFormElement} form */
function bindForm(form) {
  if (form.dataset.bound) return;
  form.dataset.bound = '1';
  const email = /** @type {HTMLInputElement | null} */ (form.querySelector('[data-zpa-wl-email]'));
  const consent = /** @type {HTMLInputElement | null} */ (form.querySelector('[data-zpa-wl-consent]'));
  const hp = /** @type {HTMLInputElement | null} */ (form.querySelector('[data-zpa-wl-hp]'));
  const btn = /** @type {HTMLButtonElement | null} */ (form.querySelector('[data-zpa-wl-submit]'));
  const d = form.dataset;

  email?.addEventListener('input', () => {
    email.removeAttribute('aria-invalid');
    say(form, '');
  });
  consent?.addEventListener('change', () => {
    consent.closest('label')?.classList.remove('is-missing');
    say(form, '');
  });

  form.addEventListener('submit', (e) => {
    e.preventDefault();
    if (d.busy) return;
    const value = (email?.value || '').trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value)) {
      email?.setAttribute('aria-invalid', 'true');
      say(form, d.errEmail || 'Zadejte e-mail.');
      email?.focus();
      return;
    }
    if (consent && !consent.checked) {
      consent.closest('label')?.classList.add('is-missing');
      say(form, d.errConsent || 'Potvrďte prosím souhlas.');
      return;
    }
    d.busy = '1';
    if (btn) btn.disabled = true;
    say(form, '');
    fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        productId: parseInt(d.productId || '0', 10),
        productHandle: d.productHandle || '',
        productTitle: d.productTitle || '',
        email: value,
        consent: true,
        locale: (document.documentElement.lang || 'cs').slice(0, 2),
        hp_company: hp ? hp.value : '',
      }),
    })
      .then((r) => r.json().catch(() => ({})).then((j) => ({ status: r.status, j })))
      .then(({ status, j }) => {
        if (status === 200 && j && j.ok) {
          done(form);
          return;
        }
        say(form, (j && j.error) || d.errGeneric || 'Něco se nepovedlo, zkuste to prosím znovu.');
      })
      .catch(() => say(form, d.errNetwork || 'Nepovedlo se odeslat. Zkontrolujte připojení.'))
      .finally(() => {
        delete d.busy;
        if (btn) btn.disabled = false;
      });
  });
}

/* ── 2. Gallery ── */
/** @param {HTMLElement} gal */
function bindGallery(gal) {
  if (gal.dataset.bound) return;
  gal.dataset.bound = '1';
  const track = /** @type {HTMLElement | null} */ (gal.querySelector('[data-zpa-track]'));
  const thumbs = /** @type {HTMLElement[]} */ (Array.from(gal.querySelectorAll('[data-zpa-thumb]')));
  if (!track || !thumbs.length) return;
  const mark = (/** @type {number} */ i) =>
    thumbs.forEach((t, k) => {
      t.classList.toggle('is-on', k === i);
      if (k === i) t.setAttribute('aria-current', 'true');
      else t.removeAttribute('aria-current');
    });
  const go = (/** @type {number} */ i) =>
    track.scrollTo({ left: Math.max(0, Math.min(thumbs.length - 1, i)) * track.clientWidth, behavior: reduced ? 'auto' : 'smooth' });
  thumbs.forEach((t, i) =>
    t.addEventListener('click', () => {
      go(i);
      mark(i);
    })
  );
  let raf = 0;
  track.addEventListener(
    'scroll',
    () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => mark(Math.round(track.scrollLeft / Math.max(1, track.clientWidth))));
    },
    { passive: true }
  );
  track.addEventListener('keydown', (e) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault();
    go(Math.round(track.scrollLeft / Math.max(1, track.clientWidth)) + (e.key === 'ArrowRight' ? 1 : -1));
  });
}

/* ── 3-5. The frame ── */
function bindFrame() {
  const root = /** @type {HTMLElement | null} */ (document.querySelector('[data-zpa-hero]'));
  if (!root || root.dataset.bound) return;
  root.dataset.bound = '1';
  const header = document.querySelector('.header');
  const col = /** @type {HTMLElement | null} */ (document.querySelector('[data-zpa-buy]'));
  const form = document.getElementById('zpa-form');
  const bar = /** @type {HTMLElement | null} */ (root.querySelector('[data-zpa-bar]'));
  const sticks = /** @type {HTMLElement[]} */ (Array.from(document.querySelectorAll('[data-zpa-stick]')));
  const stopAt = document.querySelector('main > .ui-test-product-list');
  const wide = window.matchMedia('(min-width: 1100px)');

  let queued = false;
  function place() {
    queued = false;
    const hb = header ? Math.max(0, Math.min(header.getBoundingClientRect().bottom, 140)) : 0;
    // The details section's columns: under the header, or (taller than the
    // window) far enough up that the bottom stays visible.
    sticks.forEach((el) => {
      const t = Math.min(hb + 20, window.innerHeight - el.offsetHeight - 24);
      el.style.setProperty('--zpa-stick', Math.round(t) + 'px');
    });
    if (!col) return;
    if (!wide.matches) {
      col.style.removeProperty('--zpa-buy-top');
      return;
    }
    const h = col.offsetHeight;
    let top = Math.min(hb + 20, window.innerHeight - h - 24);
    if (stopAt) top = Math.min(top, stopAt.getBoundingClientRect().top - 40 - h);
    col.style.setProperty('--zpa-buy-top', Math.round(top) + 'px');
  }
  const queue = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(place);
  };
  place();
  window.addEventListener('scroll', queue, { passive: true });
  window.addEventListener('resize', queue);
  if (window.ResizeObserver) {
    const ro = new ResizeObserver(queue);
    if (col) ro.observe(col);
    sticks.forEach((el) => ro.observe(el));
  }

  // Anchor links (the loyalty panel's "Jak to funguje", the bar): scroll
  // smoothly; a question opens; the bar's target focuses the e-mail field.
  document.querySelectorAll('a[href^="#"][data-zpa-anchor], a[data-zpa-bar-cta]').forEach((a) =>
    a.addEventListener('click', (e) => {
      const hash = /** @type {HTMLAnchorElement} */ (a).hash;
      const t = hash ? document.getElementById(decodeURIComponent(hash.slice(1))) : null;
      if (!t) return;
      e.preventDefault();
      if (t.tagName === 'DETAILS') /** @type {HTMLDetailsElement} */ (t).open = true;
      t.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: t === form ? 'center' : 'start' });
      if (t === form) {
        const input = /** @type {HTMLInputElement | null} */ (form.querySelector('[data-zpa-wl-email]'));
        if (input) setTimeout(() => input.focus({ preventScroll: true }), reduced ? 0 : 450);
      }
    })
  );

  // Bottom bar: below 1100 px, once the form has scrolled above the screen,
  // until the footer, and never after the address was sent.
  if (bar && form && 'IntersectionObserver' in window) {
    let past = false;
    let atEnd = false;
    let sent = false;
    const show = () => {
      const on = !wide.matches && past && !atEnd && !sent;
      bar.classList.toggle('is-on', on);
      bar.setAttribute('aria-hidden', on ? 'false' : 'true');
      if (on) bar.removeAttribute('inert');
      else bar.setAttribute('inert', '');
    };
    new IntersectionObserver((entries) => {
      const e = entries[entries.length - 1];
      past = !e.isIntersecting && e.boundingClientRect.top < 0;
      show();
    }).observe(form);
    const foot = document.querySelector('footer, .footer, [id*="footer"]');
    if (foot) {
      new IntersectionObserver((entries) => {
        atEnd = entries[entries.length - 1].isIntersecting;
        show();
      }).observe(foot);
    }
    document.addEventListener('zpa:sent', () => {
      sent = true;
      show();
    });
    if (wide.addEventListener) wide.addEventListener('change', show);
  }
}

function init() {
  document.querySelectorAll('form[data-zpa-wl]').forEach((f) => bindForm(/** @type {HTMLFormElement} */ (f)));
  document.querySelectorAll('[data-zpa-gal]').forEach((g) => bindGallery(/** @type {HTMLElement} */ (g)));
  bindFrame();
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
else init();
document.addEventListener('shopify:section:load', init);

/* Ingredient descriptions (sections/zpa-details.liquid): kept inside the screen,
   opened by a tap on phones, closed by a tap elsewhere or Escape. */
(() => {
  const tips = document.querySelectorAll('.zpa-inci__i.has-tip');
  if (!tips.length) return;
  const place = (el) => {
    const tip = el.querySelector('.zpa-inci__tip');
    if (!tip) return;
    el.style.setProperty('--tip-dx', '0px');
    const r = tip.getBoundingClientRect();
    const pad = 12;
    let dx = 0;
    if (r.left < pad) dx = pad - r.left;
    else if (r.right > window.innerWidth - pad) dx = window.innerWidth - pad - r.right;
    el.style.setProperty('--tip-dx', `${Math.round(dx)}px`);
  };
  const closeAll = (except) => tips.forEach((t) => { if (t !== except) t.classList.remove('is-open'); });
  tips.forEach((el) => {
    el.addEventListener('mouseenter', () => place(el));
    el.addEventListener('focus', () => place(el));
    el.addEventListener('click', (e) => {
      e.stopPropagation();
      const open = !el.classList.contains('is-open');
      closeAll(el);
      el.classList.toggle('is-open', open);
      if (open) place(el);
    });
  });
  document.addEventListener('click', () => closeAll(null));
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') { closeAll(null); if (document.activeElement && document.activeElement.classList.contains('has-tip')) document.activeElement.blur(); } });
})();
